<template>
  <div
    :id="safeId()"
    class="tabs"
    :class="{ row: vertical, 'no-gutters': vertical && card }"
  >
    <div
      key="bv-tabs-nav"
      :class="[{ 'card-header': card && !vertical && !end, 'card-footer': card && !vertical && end, 'col-auto': vertical }, navWrapperClass]"
    >
      <b-nav
        :id="safeId('_BV_tab_controls_')"
        ref="nav"
        :class="localNavClass"
        role="tablist"
        :fill="fill"
        :justified="justified"
        :align="align"
        :tabs="!noNavStyle && !pills"
        :pills="!noNavStyle && pills"
        :vertical="vertical"
        :small="small"
        :card-header="card && !vertical"
      >
        <b-tab-button
          v-for="($tab, index) in $tabs"
          :id="$tab.controlledBy || (safeId ? safeId('_BV_tab_button_') : null)"
          :key="$tab._uid || index"
          ref="buttons"
          :ref_for="true"
          :controls="safeId ? safeId() : null"
          :no-key-nav="noKeyNav"
          :pos-in-set="index + 1"
          :set-size="$tabs.length"
          :tab="$tab"
          :tab-index="getTabIndex($tab, index)"
          @click="clickTab($tab, $event)"
          @first="firstTab"
          @prev="previousTab"
          @next="nextTab"
          @last="lastTab"
        />
      </b-nav>
    </div>
    <div
      :id="safeId('_BV_tab_container_')"
      ref="content"
      class="tab-content"
      :class="{ col: vertical, [contentClass]: true }"
    >
      <slot />
    </div>
  </div>
</template>

<script>
import { BvEvent } from './bv-event'
import BTabButton from './BTabButton.vue'
import BNav from './BNav.vue'

const notDisabled = tab => !tab.disabled

const toInteger = (value, defaultValue = NaN) => {
  const integer = parseInt(value, 10)
  return isNaN(integer) ? defaultValue : integer
}

const isEvent = value => value instanceof Event
const arrayIncludes = (array, value) => array.indexOf(value) !== -1

// `requestAnimationFrame()` convenience method
export const requestAF = (
  window.requestAnimationFrame
  || window.webkitRequestAnimationFrame
  || window.mozRequestAnimationFrame
  || window.msRequestAnimationFrame
  || window.oRequestAnimationFrame
  // Fallback, but not a true polyfill
  // Only needed for Opera Mini
  || (cb => setTimeout(cb, 16))
).bind(window)

const isElement = el => !!(el && el.nodeType === Node.ELEMENT_NODE)
// const arrayFrom = (...args) => Array.from(...args)

const HAS_MUTATION_OBSERVER_SUPPORT
  = typeof MutationObserver !== 'undefined'
  || typeof WebKitMutationObserver !== 'undefined'
  || typeof MozMutationObserver !== 'undefined'

export const warnNoMutationObserverSupport = source => {
  if (HAS_MUTATION_OBSERVER_SUPPORT) {
    return false
  } else {
    console.log(`${source}: Requires MutationObserver support.`)
    return true
  }
}

// Select all elements matching selector. Returns `[]` if none found
export const selectAll = (selector) =>
  Array.from(document.querySelectorAll(selector))

export const MutationObs
  = window.MutationObserver || window.WebKitMutationObserver || window.MozMutationObserver || null

const observeDom = (
  el,
  callback,
  options
) => {
  // Handle cases where we might be passed a Vue instance
  el = el ? el.$el || el : null

  // Early exit when we have no element
  if (!isElement(el)) {
    return null
  }

  // Exit and throw a warning when `MutationObserver` isn't available
  if (warnNoMutationObserverSupport('observeDom')) {
    return null
  }

  // Define a new observer
  const obs = new MutationObs(mutations => {
    let changed = false

    // A mutation can contain several change records, so we loop
    // through them to see what has changed
    // We break out of the loop early if any "significant" change
    // has been detected
    for (let i = 0; i < mutations.length && !changed; i++) {
      // The mutation record
      const mutation = mutations[i]
      // Mutation type
      const type = mutation.type
      // DOM node (could be any DOM node type - HTMLElement, Text, comment, etc.)
      const target = mutation.target

      // Detect whether a change happened based on type and target
      if (type === 'characterData' && target.nodeType === Node.TEXT_NODE) {
        // We ignore nodes that are not TEXT (i.e. comments, etc.)
        // as they don't change layout
        changed = true
      } else if (type === 'attributes') {
        changed = true
      } else if (
        type === 'childList'
        && (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0)
      ) {
        // This includes HTMLElement and text nodes being
        // added/removed/re-arranged
        changed = true
      }
    }

    // We only call the callback if a change that could affect
    // layout/size truly happened
    if (changed) {
      callback()
    }
  })

  // Have the observer observe foo for changes in children, etc
  obs.observe(el, { childList: true, subtree: true, ...options })

  // We return a reference to the observer so that `obs.disconnect()`
  // can be called if necessary
  // To reduce overhead when the root element is hidden
  return obs
}

export default {
  name: 'BTabs',
  components: {
    BNav,
    BTabButton
  },
  provide() {
    return {
      getBvTabs: () => this
    }
  },
  props: {
    align: {
      type: String,
      default: ''
    },
    fill: Boolean,
    justified: Boolean,
    pills: Boolean,
    small: Boolean,
    vertical: Boolean,
    // Only applied to the currently active `<b-nav-item>`
    activeNavItemClass: {
      type: [String, Array, Object],
      default: null
    },
    // Only applied to the currently active `<b-tab>`
    // This prop is sniffed by the `<b-tab>` child
    activeTabClass: {
      type: [String, Array, Object],
      default: null
    },
    card: Boolean,
    contentClass: {
      type: [String, Array, Object],
      default: null
    },
    // Synonym for 'bottom'
    end: Boolean,
    // This prop is sniffed by the `<b-tab>` child
    lazy: Boolean,
    navClass: {
      type: [String, Array, Object],
      default: null
    },
    navWrapperClass: {
      type: [String, Array, Object],
      default: null
    },
    noFade: Boolean,
    noKeyNav: Boolean,
    noNavStyle: Boolean,
    tag: {
      type: String,
      default: 'div'
    },
    modelValue: {
      type: Number,
      default: null
    },
    id: {
      type: String,
      default: null
    }

  },
  emits: ['active', 'update:active', 'changed', 'input', 'click', 'update:modelValue', 'activate-tab'],
  data() {
    return {
      // Array of direct child `<b-tab>` instances, in DOM order
      tabs: [],
      // Index of current tab
      currentTab: toInteger(this.modelValue, -1),
      // Array of child instances registered (for triggering reactive updates)
      registeredTabs: [],
      localId_: null
    }
  },
  computed: {
    fade() {
      // This computed prop is sniffed by the tab child
      return !this.noFade
    },
    localNavClass() {
      const classes = []
      if (this.card && this.vertical) {
        classes.push('card-header', 'h-100', 'border-bottom-0', 'rounded-0')
      }
      return [...classes, this.navClass]
    },
    $tabs() {
      return this.getTabs()
    },
    safeId() {
      // Computed property that returns a dynamic function for creating the ID
      // Reacts to changes in both `.id` and `.localId_` and regenerates a new function
      const id = this.id || this.localId_

      // We return a function that accepts an optional suffix string
      // So this computed prop looks and works like a method
      // but benefits from Vue's computed prop caching
      const fn = suffix => {
        if (!id) {
          return null
        }
        suffix = String(suffix || '').replace(/\s+/g, '_')
        return suffix ? id + '_' + suffix : id
      }
      return fn
    },
    $activeTab() {
      return this.$tabs.find($tab => $tab.localActive && !$tab.disabled)
    },
    $fallbackTab() {
      return this.$tabs.find($tab => !$tab.disabled)
    }
  },
  watch: {
    modelValue(newValue, oldValue) {
      if (!this.tabs.length) return
      if (newValue !== oldValue) {
        newValue = toInteger(newValue, -1)
        oldValue = toInteger(oldValue, 0)

        const $tab = this.tabs[newValue]
        if ($tab && !$tab.disabled) {
          this.activateTab($tab)
        } else {
          // Try next or prev tabs
          if (newValue < oldValue) {
            this.previousTab()
          } else {
            this.nextTab()
          }
        }
      }
    },
    currentTab(newValue) {
      let index = -1
      // Ensure only one tab is active at most
      this.tabs.forEach(($tab, i) => {
        if (i === newValue && !$tab.disabled) {
          $tab.localActive = true
          index = i
        } else {
          $tab.localActive = false
        }
      })

      // Update the v-model
      this.$emit('update:modelValue', index)
    },
    // If tabs added, removed, or re-ordered, we emit a `changed` event
    tabs(newValue, oldValue) {
      // We use `_uid` instead of `safeId()`, as the later is changed in a `$nextTick()`
      // if no explicit ID is provided, causing duplicate emits
      // if (newValue.map($tab => $tab._uid) !== oldValue.map($tab => $tab._uid)) {
      // In a `$nextTick()` to ensure `currentTab` has been set first
      this.$nextTick(() => {
        // We emit shallow copies of the new and old arrays of tabs,
        // to prevent users from potentially mutating the internal arrays
        this.$emit('changed', newValue.slice(), oldValue.slice())
      })
      // }
    },
    // Each `<b-tab>` will register/unregister itself
    // We use this to detect when tabs are added/removed
    // to trigger the update of the tabs
    registeredTabs() {
      this.updateTabs()
    }
  },
  created() {
    // Create private non-reactive props
    this.$_observer = null
  },
  mounted() {
    this.localId_ = this.$uuid
    this.setObserver(true)
  },
  beforeUnmount() {
    this.setObserver(false)
    // Ensure no references to child instances exist
    this.tabs = []
  },
  methods: {
    getTabIndex($tab, index) {
      let tabIndex = null
      if (!this.noKeyNav) {
        tabIndex = -1
        if ($tab === this.$activeTab || (!this.$activeTab && $tab === this.$fallbackTab)) {
          tabIndex = null
        }
      }
      return tabIndex
    },
    registerTab($tab) {
      if (!arrayIncludes(this.registeredTabs, $tab)) {
        this.registeredTabs.push($tab)
      }
    },
    unregisterTab($tab) {
      this.registeredTabs = this.registeredTabs.slice().filter($t => $t !== $tab)
    },
    // DOM observer is needed to detect changes in order of tabs
    setObserver(on = true) {
      if (this.$_observer) {
        this.$_observer.disconnect()
      }
      this.$_observer = null

      if (on) {
        const handler = () => {
          this.$nextTick(() => {
            requestAF(() => {
              this.updateTabs()
            })
          })
        }

        // Watch for changes to `<b-tab>` sub components
        this.$_observer = observeDom(this.$refs.content, handler, {
          childList: true,
          subtree: false,
          attributes: true,
          attributeFilter: ['id']
        })
      }
    },
    getTabs() {
      return this.registeredTabs
    },
    updateTabs() {
      const $tabs = this.getTabs()
      if (!$tabs.length) return
      if ($tabs[this.modelValue] && !$tabs[this.modelValue].disabled) {
        $tabs[this.modelValue].localActive = true
      }

      // Find last active non-disabled tab in current tabs
      // We trust tab state over `currentTab`, in case tabs were added/removed/re-ordered
      let tabIndex = $tabs.indexOf(
        $tabs
          .slice()
          .reverse()
          .find($tab => $tab.localActive && !$tab.disabled)
      )

      // Else try setting to `currentTab`
      if (tabIndex < 0) {
        const { currentTab } = this
        if (currentTab >= $tabs.length) {
          // Handle last tab being removed, so find the last non-disabled tab
          tabIndex = $tabs.indexOf(
            $tabs
              .slice()
              .reverse()
              .find(notDisabled)
          )
        } else if ($tabs[currentTab] && !$tabs[currentTab].disabled) {
          // Current tab is not disabled
          tabIndex = currentTab
        }
      }

      // Else find first non-disabled tab in current tabs
      if (tabIndex < 0) {
        tabIndex = $tabs.indexOf($tabs.find(notDisabled))
      }

      // Ensure only one tab is active at a time
      $tabs.forEach(($tab, index) => {
        $tab.localActive = index === tabIndex
      })

      this.tabs = $tabs
      this.currentTab = tabIndex
    },
    // Find a button that controls a tab, given the tab reference
    // Returns the button vm instance
    getButtonForTab($tab) {
      return (this.$refs.buttons || []).find($btn => $btn.tab === $tab)
    },
    // Force a button to re-render its content, given a `<b-tab>` instance
    // Called by `<b-tab>` on `update()`
    updateButton($tab) {
      const $button = this.getButtonForTab($tab)
      if ($button && $button.$forceUpdate) {
        $button.$forceUpdate()
      }
    },
    // Activate a tab given a `<b-tab>` instance
    // Also accessed by `<b-tab>`
    activateTab($tab) {
      const { currentTab, tabs: $tabs } = this
      let result = false

      if ($tab) {
        const index = $tabs.indexOf($tab)
        if (index !== currentTab && index > -1 && !$tab.disabled) {
          const tabEvent = new BvEvent('activate-tab', {
            cancelable: true,
            vueTarget: this,
            componentId: this.safeId()
          })

          this.$emit(tabEvent.type, index, currentTab, tabEvent)

          if (!tabEvent.defaultPrevented) {
            this.currentTab = index
            result = true
          }
        }
      }

      // Couldn't set tab, so ensure v-model is up to date
      if (!result && this.modelValue !== currentTab) {
        this.$emit('update:modelValue', currentTab)
      }

      return result
    },
    // Deactivate a tab given a `<b-tab>` instance
    // Accessed by `<b-tab>`
    deactivateTab($tab) {
      if ($tab) {
        // Find first non-disabled tab that isn't the one being deactivated
        // If no tabs are available, then don't deactivate current tab
        return this.activateTab(this.tabs.filter($t => $t !== $tab).find(notDisabled))
      }
      return false
    },
    // Focus a tab button given its `<b-tab>` instance
    focusButton($tab) {
      // Wrap in `$nextTick()` to ensure DOM has completed rendering
      this.$nextTick(() => {
        try {
          // Focus the tab button
          this.getButtonForTab($tab)?.focus()
        } catch (err) {
          // Ignore
        }
      })
    },
    // Emit a click event on a specified `<b-tab>` component instance
    emitTabClick(tab, event) {
      if (isEvent(event) && tab && tab.$emit && !tab.disabled) {
        tab.$emit('click', event)
      }
    },
    // Click handler
    clickTab($tab, event) {
      this.activateTab($tab)
      this.emitTabClick($tab, event)
    },
    // Move to first non-disabled tab
    firstTab(focus) {
      const $tab = this.tabs.find(notDisabled)
      if (this.activateTab($tab) && focus) {
        this.focusButton($tab)
        this.emitTabClick($tab, focus)
      }
    },
    // Move to previous non-disabled tab
    previousTab(focus) {
      const currentIndex = Math.max(this.currentTab, 0)
      const $tab = this.tabs
        .slice(0, currentIndex)
        .reverse()
        .find(notDisabled)
      if (this.activateTab($tab) && focus) {
        this.focusButton($tab)
        this.emitTabClick($tab, focus)
      }
    },
    // Move to next non-disabled tab
    nextTab(focus) {
      const currentIndex = Math.max(this.currentTab, -1)
      const $tab = this.tabs.slice(currentIndex + 1).find(notDisabled)
      if (this.activateTab($tab) && focus) {
        this.focusButton($tab)
        this.emitTabClick($tab, focus)
      }
    },
    // Move to last non-disabled tab
    lastTab(focus) {
      const $tab = this.tabs
        .slice()
        .reverse()
        .find(notDisabled)
      if (this.activateTab($tab) && focus) {
        this.focusButton($tab)
        this.emitTabClick($tab, focus)
      }
    }
  }
}
</script>
